// frontend/src/pages/usuarios/EspecialistasFormPage.jsx
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';

function EspecialistasFormPage() {
  const [nome, setNome] = useState('');
  const [loading, setLoading] = useState(false);        // loading de tela/submit
  const [initialLoading, setInitialLoading] = useState(false); // loading do fetch inicial (edição)
  const [error, setError] = useState(null);             // pode ser { kind: 'permission' } ou erro genérico
  const abortRef = useRef(null);

  const navigate = useNavigate();
  const { id } = useParams();

  const pageTitle = id ? 'Editar Especialista' : 'Novo Especialista';
  const pageSubtitle = id ? `Editando o registro #${id}` : 'Preencha os dados para criar um novo especialista';

  // Carrega dados para edição
  useEffect(() => {
    if (!id) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const load = async () => {
      setInitialLoading(true);
      setError(null);
      try {
        const resp = await api.get(`/especialistas/${id}`, { signal: controller.signal });
        const payload = resp?.data?.data || resp?.data || {};
        setNome(payload?.nome || '');
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setInitialLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [id]);

  // Submit (criar/atualizar)
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    setLoading(true);
    setError(null);

    let ordem = 1;
    if (!id) {
      // Best-effort para definir ordem (se falhar, mantém 1)
      try {
        const response = await api.get('/especialistas', {
          params: {
            per_page: 10000,
            status: 'active',
          },
        });
        const lista = response?.data?.data || response?.data || [];
        const maxOrdem = Array.isArray(lista)
          ? lista.reduce((max, esp) => (Number(esp?.ordem) > max ? Number(esp?.ordem) : max), 0)
          : 0;
        ordem = maxOrdem + 1;
      } catch {
        // silencioso; backend pode validar/ajustar
      }
    }

    const payload = { nome };
    if (!id) payload.ordem = ordem;

    try {
      if (id) {
        await api.put(`/especialistas/${id}`, payload);
      } else {
        await api.post('/especialistas', payload);
      }
      navigate('/painel/especialistas');
    } catch (err) {
      if (err?.response?.status === 403) {
        setError({ kind: 'permission' });
      } else if (err?.response?.data?.message) {
        // Ignora especificamente mensagens relacionadas à 'ordem' se o backend exigir
        const msg = String(err.response.data.message || '');
        setError(msg.toLowerCase().includes('ordem') ? null : err);
      } else {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  // GATES
  if (initialLoading) {
    return (
      <div className="especialistas-form-page">
        <LoadingSpinner text="Carregando dados do especialista..." />
      </div>
    );
  }
  if (error?.kind === 'permission') {
    return (
      <div className="especialistas-form-page">
        <PermissionDeniedAlert action={id ? 'editar especialista' : 'criar especialista'} />
      </div>
    );
  }

  return (
    <div className="especialistas-form-page">
      <div className="content-header">
        <h1 className="content-title">{pageTitle}</h1>
        <p className="content-subtitle">{pageSubtitle}</p>
      </div>

      <div className="card especialistas-form-card">
        <div className="card-body">
          {/* Erros não-permission exibidos de forma elegante */}
          {error && error.kind !== 'permission' && (
            <AlertException error={error} fallback="Ocorreu um erro ao salvar/carregar o especialista." />
          )}

          <form className="especialistas-form" onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="nome" className="form-label">Nome do Especialista</label>
              <input
                type="text"
                className="form-control"
                id="nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
                maxLength={255}
              />
            </div>

            <div className="especialistas-form-actions d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => navigate('/painel/especialistas')}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EspecialistasFormPage;
